import dayjs from 'dayjs';
import {
  PAYMENT_AMOUNT_TYPE_ENUM,
  PAYMENT_DUE_TYPE_ENUM,
  PAYMENT_DUE_TYPE_LIST
} from '../config/constants';
import { currencyFormat, singularPluralFormat } from './format';

const subtractDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() - days);
  return result;
};

const subtractMonths = (date, months) => {
  const result = new Date(date);
  result.setMonth(result.getMonth() - months);
  return result;
};

const calculateLatestRegistrationDate = (installments) => {
  const sorted = installments
    .filter((i) => !i.deleted)
    .sort((a, b) => a.sort - b.sort);

  let latestDate = null;
  let hasConstraint = false;

  for (let i = sorted.length - 1; i >= 0; i--) {
    const installment = sorted[i];

    switch (installment.fkPaymentDueType?.toString()) {
      case PAYMENT_DUE_TYPE_ENUM.FIXED_DATE:
        latestDate = new Date(installment.fixedDueDate);
        hasConstraint = true;
        break;

      case PAYMENT_DUE_TYPE_ENUM.DAYS_AFTER_PREVIOUS:
        if (latestDate) {
          latestDate = subtractDays(latestDate, installment.dueValue + 1);
        }
        break;

      case PAYMENT_DUE_TYPE_ENUM.MONTHS_AFTER_PREVIOUS:
        if (latestDate) {
          latestDate = subtractMonths(latestDate, installment.dueValue);
          latestDate = subtractDays(latestDate, 1);
        }
        break;

      case PAYMENT_DUE_TYPE_ENUM.DAYS_AFTER_REGISTRATION:
        if (latestDate) {
          return subtractDays(latestDate, installment.dueValue);
        }

        return null;

      case PAYMENT_DUE_TYPE_ENUM.MONTHS_AFTER_REGISTRATION:
        if (latestDate) {
          return subtractMonths(latestDate, installment.dueValue);
        }

        return null;

      case PAYMENT_DUE_TYPE_ENUM.IMMEDIATE:
        return latestDate;

      default:
        break;
    }
  }

  return latestDate;
};

const calculateMinimumPaymentPlanCheckoutTotal = (installments) => {
  let totalFixed = 0;
  let totalPercentage = 0;

  for (const installment of installments) {
    switch (installment.fkPaymentAmountType?.toString()) {
      case PAYMENT_AMOUNT_TYPE_ENUM.FIXED:
        totalFixed += installment.amount ?? 0;
        break;

      case PAYMENT_AMOUNT_TYPE_ENUM.PERCENTAGE:
        totalPercentage += installment.amount ?? 0;
        break;

      default:
        break;
    }
  }

  if (totalPercentage >= 10000) {
    return totalFixed === 0 ? 0 : Number.POSITIVE_INFINITY;
  }

  const percentage = totalPercentage / 10000;

  return Math.ceil(totalFixed / (1 - percentage));
};

const getPaymentPlanInstallmentSetupError = (installment, installments) => {
  let error = null;
  const fkPaymentDueType = installment.fkPaymentDueType?.toString();
  const fkPaymentAmountType = installment.fkPaymentAmountType?.toString();

  const installmentDueType = PAYMENT_DUE_TYPE_LIST.find(
    (dueType) => dueType.value === fkPaymentDueType
  );

  if (installmentDueType?.isFirstInstallmentOnly) {
    if (installment.sort !== 1) {
      error =
        'This payment due type can only be set for the first installment.';
    }
  }

  if (installmentDueType?.isSubsequentInstallmentsOnly) {
    if (installment.sort === 1) {
      error =
        'This payment due type can only be set for subsequent installments.';
    }
  }

  if (fkPaymentDueType === PAYMENT_DUE_TYPE_ENUM.FIXED_DATE) {
    if (!installment.fixedDueDate) {
      error = 'Fixed date is required for fixed date payment due type.';
    }

    if (
      installment.fixedDueDate &&
      new Date(installment.fixedDueDate) < new Date()
    ) {
      error = 'Fixed date cannot be in the past.';
    }

    if (installment.sort > 1) {
      const previousInstallment = installments.find(
        (i) =>
          i.fkPaymentDueType?.toString() === PAYMENT_DUE_TYPE_ENUM.FIXED_DATE &&
          i.sort < installment.sort &&
          new Date(i.fixedDueDate) > new Date(installment.fixedDueDate)
      );

      if (previousInstallment) {
        error = 'Fixed date must be after previous fixed date installments.';
      }
    }
  }

  if (fkPaymentAmountType === PAYMENT_AMOUNT_TYPE_ENUM.FIXED) {
    if (!installment.amount || installment.amount <= 1000) {
      error = 'Amount must be greater than $10 for fixed payment amount type.';
    }
  }

  if (fkPaymentAmountType === PAYMENT_AMOUNT_TYPE_ENUM.PERCENTAGE) {
    if (!installment.amount || installment.amount <= 0) {
      error =
        'Percentage must be greater than 0% for percentage payment amount type.';
    }

    if (installment.amount > 10000) {
      error = 'Percentage cannot be greater than 100%.';
    }
  }

  if (fkPaymentAmountType === PAYMENT_AMOUNT_TYPE_ENUM.REMAINING_BALANCE) {
    if (installment.sort !== installments.length) {
      error = 'Remaining balance can only be set for the last installment.';
    }
  }

  return error;
};

const getPaymentPlanInstallmentInfo = (installment, installments) => {
  const error = getPaymentPlanInstallmentSetupError(installment, installments);

  let paymentAmountInfo = '';
  switch (installment.fkPaymentAmountType?.toString()) {
    case PAYMENT_AMOUNT_TYPE_ENUM.FIXED:
      paymentAmountInfo = `${currencyFormat(
        installment.amount / 100
      )} fixed amount`;
      break;
    case PAYMENT_AMOUNT_TYPE_ENUM.PERCENTAGE:
      paymentAmountInfo = `${(installment.amount / 100).toFixed(0)}% of total`;
      break;
    case PAYMENT_AMOUNT_TYPE_ENUM.REMAINING_BALANCE:
      paymentAmountInfo = 'Remaining Balance';
      break;
    default:
      paymentAmountInfo = '';
  }

  let paymentDateInfo = '';
  switch (installment.fkPaymentDueType?.toString()) {
    case PAYMENT_DUE_TYPE_ENUM.FIXED_DATE:
      paymentDateInfo = `Fixed Date: ${dayjs(installment.fixedDueDate).format(
        'MMM D, YYYY'
      )}`;
      break;
    case PAYMENT_DUE_TYPE_ENUM.IMMEDIATE:
      paymentDateInfo = 'Immediate';
      break;
    case PAYMENT_DUE_TYPE_ENUM.DAYS_AFTER_PREVIOUS:
      paymentDateInfo = `Due ${singularPluralFormat(
        installment.dueValue,
        'day',
        'days'
      )} after previous`;
      break;
    case PAYMENT_DUE_TYPE_ENUM.MONTHS_AFTER_PREVIOUS:
      paymentDateInfo = `Due ${singularPluralFormat(
        installment.dueValue,
        'month',
        'months'
      )} after previous`;
      break;
    case PAYMENT_DUE_TYPE_ENUM.DAYS_AFTER_REGISTRATION:
      paymentDateInfo = `Due ${singularPluralFormat(
        installment.dueValue,
        'day',
        'days'
      )} after registration`;
      break;
    case PAYMENT_DUE_TYPE_ENUM.MONTHS_AFTER_REGISTRATION:
      paymentDateInfo = `Due ${singularPluralFormat(
        installment.dueValue,
        'month',
        'months'
      )} after registration`;
      break;
    default:
      paymentDateInfo = '';
  }

  return {
    paymentAmountInfo,
    paymentDateInfo,
    error
  };
};

const getPaymentPlanInfo = (paymentPlan) => {
  const installmentInfo = paymentPlan.regAssociationPaymentPlanInstallments.map(
    (installment) =>
      getPaymentPlanInstallmentInfo(
        installment,
        paymentPlan.regAssociationPaymentPlanInstallments
      )
  );

  const totalFlatAmount = paymentPlan.regAssociationPaymentPlanInstallments.reduce(
    (total, i) => {
      if (
        i.fkPaymentAmountType?.toString() === PAYMENT_AMOUNT_TYPE_ENUM.FIXED
      ) {
        return total + i.amount;
      }
      return total;
    },
    0
  );

  const totalPercentage = paymentPlan.regAssociationPaymentPlanInstallments.reduce(
    (total, i) => {
      if (
        i.fkPaymentAmountType?.toString() ===
        PAYMENT_AMOUNT_TYPE_ENUM.PERCENTAGE
      ) {
        return total + i.amount;
      }
      return total;
    },
    0
  );

  const hasRemainingBalanceInstallment = paymentPlan.regAssociationPaymentPlanInstallments.some(
    (i) =>
      i.fkPaymentAmountType?.toString() ===
      PAYMENT_AMOUNT_TYPE_ENUM.REMAINING_BALANCE
  );

  const paymentPlanErrors = [];
  if (totalFlatAmount > 0) {
    if (!hasRemainingBalanceInstallment) {
      paymentPlanErrors.push({
        key: 'missingRemainingBalance',
        message:
          'Payment plan has fixed installments, but is missing a remaining balance installment.'
      });
    }

    if (totalPercentage >= 10000) {
      paymentPlanErrors.push({
        key: 'fixedAndPercentageExceeds100',
        message:
          'Payment plan has fixed installments and percentage installments that exceed 100%.'
      });
    }
  }

  if (totalPercentage > 0) {
    if (totalPercentage > 10000) {
      paymentPlanErrors.push({
        key: 'percentageExceeds100',
        message: 'Total percentage of installments exceeds 100%.'
      });
    }

    if (
      totalFlatAmount === 0 &&
      !hasRemainingBalanceInstallment &&
      totalPercentage < 10000
    ) {
      paymentPlanErrors.push({
        key: 'percentageLessThan100',
        message: 'Percentage installments do not add up to 100%.'
      });
    }
  }

  const hasInstallments =
    paymentPlan.regAssociationPaymentPlanInstallments.length > 0;
  if (!hasInstallments) {
    paymentPlanErrors.push({
      key: 'noInstallments',
      message: 'Payment plan has no installments.'
    });
  }

  const hasInstallmentErrors = installmentInfo.some((info) => info.error);
  const isValidPaymentPlan =
    hasInstallments && !hasInstallmentErrors && paymentPlanErrors.length === 0;

  const minimumEligibleCheckoutTotal = calculateMinimumPaymentPlanCheckoutTotal(
    paymentPlan.regAssociationPaymentPlanInstallments
  );

  const latestCheckoutDate = calculateLatestRegistrationDate(
    paymentPlan.regAssociationPaymentPlanInstallments
  );

  return {
    isValidPaymentPlan,
    hasInstallmentErrors,
    installmentInfo,
    totalFlatAmount,
    totalPercentage,
    paymentPlanErrors,
    minimumEligibleCheckoutTotal,
    latestCheckoutDate
  };
};

const getCheckoutPaymentPlanInfo = (paymentPlan) => {
  if (!paymentPlan.installments || paymentPlan.installments.length === 0) {
    return {
      serviceFeeLabel: '',
      paymentPlanLabel: ''
    };
  }

  const firstAmount = paymentPlan.installments[0].totalInCents;
  const areAllPaymentsSameAmount = paymentPlan.installments.every(
    (i) => Math.abs(i.totalInCents - firstAmount) <= 1
  );

  let serviceFeeLabel = '';
  if (paymentPlan.additionalPercentageFee) {
    serviceFeeLabel += `${paymentPlan.additionalPercentageFee * 100}%`;
  }
  if (paymentPlan.additionalFlatFeeInCents) {
    if (paymentPlan.additionalPercentageFee) {
      serviceFeeLabel += ' +';
    }
    serviceFeeLabel += `${currencyFormat(
      paymentPlan.additionalFlatFeeInCents / 100
    )}`;
  }

  let paymentPlanLabel = ``;
  if (areAllPaymentsSameAmount) {
    paymentPlanLabel += `${
      paymentPlan.installments.length
    } payments of ${currencyFormat(
      paymentPlan.installments[0].totalInCents / 100
    )}`;
  }

  return {
    serviceFeeLabel,
    paymentPlanLabel
  };
};

export {
  getPaymentPlanInstallmentInfo,
  getPaymentPlanInstallmentSetupError,
  getPaymentPlanInfo,
  getCheckoutPaymentPlanInfo
};
